import { AREA_PAGE_FILTER_OPTION_TO_GTM_EVENT } from 'constants/area-map-page-filters';
import {
  GTM_AREA_PAGE_MAP_CARD_FOURTH_CLICK,
  GTM_AREA_PAGE_MAP_CARD_FOURTH_VERSION_A,
  GTM_AREA_PAGE_MAP_CARD_FOURTH_VERSION_B,
  GTM_CLICK_AREA_PAGE_MAP_BUTTON,
  GTM_CLICK_AREA_PAGE_MORE_FILTERS_BUTTON,
  GTM_CLICK_AREA_PAGE_SAVE_SEARCH_BUTTON,
  GTM_VIEW_AREA_PAGE,
  GTM_CLICK_AREA_PAGE_NO_RESULTS,
} from 'constants/events';
import {
  useUserContext,
} from 'contexts/user';
import {
  CREATE_SAVED_SEARCH_MODAL,
  LOGIN_REGISTRATION_MODAL,
  SAVED_SEARCH_FILTERS_MODAL,
  useModalContext,
} from 'contexts/modal';
import { getTenantById } from 'tenants';
import { useTenantId } from 'hooks/use-tenant-id';
import { useAreaPageObservability } from 'hooks/useAreaPageObservability';
import { ListingObservabilityService } from 'observability/services/listing-observability';
import {
  AVAILABLE_STATUS,
  NOT_AVAILABLE_OTHER_STATUS,
  Filter,
  FilterKeys,
  Sort,
  DEFAULT_HAS_IMAGE_VALUE,
} from 'contexts/preferences/listing-params/types';
import { diff } from 'deep-object-diff';
import deepmerge from 'deepmerge';
import { cloneDeep } from 'lodash';
import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CountryCodeList } from 'types/countries';
import { hasFilterDeprecationBeenAck, setFilterDeprecationAck } from 'utils/ack_filter_deprecation';
import { hasNoImageListingsFilterBeenAck, setNoImageListingsFilterAck } from 'utils/ack_no_image_listings_filter';
import { getPositionFromDescription, getPoiPositionFromDescription } from 'utils/google-maps/getPositionFromDescription';
import { trackEvent } from 'utils/google-tag-manager';
import { isClientSide } from 'utils/host-config';
import { hasDeprecatedHomeType } from 'utils/listing-query-helper';
import { isUsStateCode, ProvinceOrStateCode, provinceOrStateSlugFromCode } from 'utils/province_or_state';
import { navigateToMilitaryBaseMapView, navigateToAddressMapView } from 'utils/redirect-to-map-view';
import { withEventTracking } from 'utils/track-event-hof';
import AreaListingsPageView, { AreaInsightsData, Pagination, LocalizedSubheadingData } from './area_listings_page_view';
import generateLocalizedSubheading from './util/generate-localized-subheading';
import { AreaPageListingCardData } from './area_page_listing_card_data';
import getTargetedUrl, { TargetedUrlHelper } from '../../../utils/targeted-url-helper';
import { DEFAULT_LISTING_PARAMS_FILTER_SHARED } from 'contexts/preferences/listing-params/defaults';
import { useIsMobile } from 'hooks/use-size-class';
import { AddressType, SingleAddress, type AreaPageListing } from '@zoocasa/go-search';
import { useShowQuebecPopup } from 'hooks/use-quebec-popup';
import { useListingsCompliance } from 'hooks/use-listings-compliance';
import { useSearchResultClick } from 'hooks/use-search-result-click';
import { useCaptureLastSearch } from 'hooks/use-capture-last-search';
import { getAreaLocationFilter } from './util/area-location-filter';
import { AREA_PAGE_IGNORE_PROFILE_SETTINGS_IMAGELESS_COOKIE_NAME, MLML_AGENT_COOKIE_NAME, MLML_LEAD_COOKIE_NAME } from 'constants/cookies';
import Cookies from 'js-cookie';
import { IGNORED_FILTER_PROPERTIES } from 'constants/filter';
import { usePreferencesContext } from 'contexts';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import type { ListingCardData } from 'components/listing-card/listing-card';
import type { PartialDeep } from 'type-fest';
import type { AddressViewModel, AreaListingsPageViewModel, MilitaryBaseViewModel } from './area_listings_page_view_model';

//#region Types
export interface AreaListingsPageControllerProps {
  viewModel: AreaListingsPageViewModel<AddressViewModel | MilitaryBaseViewModel>;
}
//#endregion

const areaPageListingToListingCardData = (listing: AreaPageListing) => (new AreaPageListingCardData(listing));

/** Memoized wrapper around the AreaListingsPageView component */
const PageView = memo(AreaListingsPageView);

const AreaListingsPageController = (props: AreaListingsPageControllerProps) => {
  const { viewModel: {
    pagination: {
      page, count, total, size,
    },
    heading,
    localizedHeading,
    areaGuideData,
    expandGuidesByDefault,
    internalLinks,
    seoLinks,
    listings,
    listingsWithCompliance, // V2 listings with server-side computed compliance
    showMapCardExperimentView,
    myLinkMyLead = false,
    useListingCardWithCompliance,
    missingAddress,
  }} = props;

  const listingsWithComplianceArray = listingsWithCompliance 
    ? (Array.isArray(listingsWithCompliance) 
      ? listingsWithCompliance 
      : Object.values(listingsWithCompliance as Record<string, any>))
    : null;

  // Reactively compute compliance for v2 listings
  const listingsWithReactiveCompliance = useListingsCompliance(listingsWithComplianceArray);

  const isMilitaryBase = 'militaryBase' in props.viewModel;

  const viewModel = props.viewModel;

  // Handle properties specific to each view model type
  const requestedAddress = isMilitaryBase ? undefined : (viewModel as AreaListingsPageViewModel<AddressViewModel>).requestedAddress;
  const electedAddress: SingleAddress = isMilitaryBase ? undefined : (viewModel as AreaListingsPageViewModel<AddressViewModel>).electedAddress;
  const militaryBase = isMilitaryBase ? (viewModel as AreaListingsPageViewModel<MilitaryBaseViewModel>).militaryBase : undefined;
  const militaryPath = isMilitaryBase ? (viewModel as AreaListingsPageViewModel<MilitaryBaseViewModel>).militaryPath : undefined;
  const breadcrumbs = isMilitaryBase ? [] : (viewModel as AreaListingsPageViewModel<AddressViewModel>).breadcrumbs;

  const { listingParams, refreshCurrentLocationFilter } = usePreferencesContext();
  const { openModal } = useModalContext();
  const { user, isAuthenticated, siteLocation } = useUserContext();
  const { t } = useTranslation(Namespaces.COMMON);
  const router = useRouter();
  const { push, query } = router;
  const tenantId = useTenantId();
  const tenant = getTenantById(tenantId);
  const useLayoutV2 = tenant?.features?.UseNewAreaSearchDesign ?? false;

  // Determine if fallback occurred (requested city has no listings, so we show province/state listings)
  const isFallbackAreaPage = !isMilitaryBase && requestedAddress?.slug !== electedAddress.slug;
  
  // For observability: use 0 when fallback occurred, since the requested city has no listings
  // Otherwise use the actual count from the requested area
  const observabilityListingCount = isFallbackAreaPage ? 0 : count;

  // Track area page load for observability
  // Use requestedAddress.subDivision.slug for city tracking to ensure we track the requested city
  // even when fallback occurs (electedAddress.slug may be province/state after fallback).
  // Province/state and country codes are still correct from electedAddress even after fallback.
  // Use 0 for listingCount when fallback occurred, since the requested city has no listings.
  useAreaPageObservability({
    citySlug: requestedAddress?.subDivision?.slug ?? electedAddress?.slug ?? '',
    provinceOrState: electedAddress?.provinceOrState ?? '',
    country: electedAddress?.countryCode ?? '',
    listingCount: observabilityListingCount,
    pageType: 'standard',
    tenant: tenant!,
  });

  // Set filter and sort based on military or area page
  const sourceFilter = isMilitaryBase ? militaryPath?.filter : requestedAddress?.filter;
  const sourceSort = isMilitaryBase ? militaryPath?.sort : requestedAddress?.sort;
  // sourceFilter carries only the URL's SEO filters (status/home type/price) — never the
  // location — and DEFAULT_LISTING_PARAMS_FILTER_SHARED blanks slug/areaName. Without the area
  // re-applied here, listingParams identifies no area at all, and every consumer that persists
  // it (captureLastSearch, the listing-params cookie, saved searches) records the Toronto/Tampa
  // default instead of the area on screen.
  const areaLocationFilter = getAreaLocationFilter({ requestedAddress, electedAddress, militaryBase });
  if (sourceFilter) { // Set filter with defaults
    const consolidatedFilter = deepmerge(DEFAULT_LISTING_PARAMS_FILTER_SHARED, sourceFilter as Filter);
    listingParams.setFilter({ ...consolidatedFilter, ...areaLocationFilter });
  } else {
    listingParams.setFilter({ ...DEFAULT_LISTING_PARAMS_FILTER_SHARED, ...areaLocationFilter });
  }
  if (sourceSort) { // Set sort if available
    listingParams.setSort(sourceSort);
  }

  const listingsData: ListingCardData[] = useMemo(() => listings.map((listing: AreaPageListing) => areaPageListingToListingCardData(listing)), [listings]);
  const paginationData: Pagination = { page: page + 1, size, count, total };
  const hasListings = count > 0;
  const showDisclaimer = (isMilitaryBase || electedAddress.countryCode === CountryCodeList.UNITED_STATES) && hasListings;

  const isStatusAvailable = listingParams.filter.status === AVAILABLE_STATUS;
  const showGuides = !isMilitaryBase && (isStatusAvailable || hasListings) && !isFallbackAreaPage && areaGuideData !== null;

  let areaOverviewTitle: string | undefined;
  let areaName: string | undefined;
  let areaBlurb: string | undefined;
  let areaBlurbHeading: string | undefined;
  let areaNamedContent: string[] | undefined;
  let areaInsightsData: AreaInsightsData | undefined;

  if (showGuides) {
    areaOverviewTitle = generateAreaOverviewTitle(electedAddress);
    areaName = electedAddress.label;
    areaBlurb = areaGuideData?.areaBlurb;
    areaBlurbHeading = areaGuideData?.areaBlurbHeading;
    areaNamedContent = areaGuideData?.areaNamedContent;
    areaInsightsData = areaGuideData?.areaInsightsData;
  }

  const showFilterDeprecationWarningInitialState = (filter: PartialDeep<Filter>) => () => isClientSide() && !hasFilterDeprecationBeenAck() && hasDeprecatedHomeType(filter);
  const showNoImageListingsFilterWarningInitialState = (hasImageFilter: boolean = DEFAULT_HAS_IMAGE_VALUE) => () => isClientSide() && !hasNoImageListingsFilterBeenAck() && hasImageFilter;

  const [showFilterDeprecationWarning, setShowFilterDeprecationWarning] = useState(showFilterDeprecationWarningInitialState(listingParams.filter));
  const [showNoImageListingsFilterWarning, setShowNoImageListingsFilterWarning] = useState(showNoImageListingsFilterWarningInitialState(listingParams.filter.hasImage));

  // Generate message for missing address redirect (when user navigates to a non-existent address page)
  // The missingAddress prop is set by the server-side props from a cookie set by the middleware
  const missingAddressMessage = useMemo(() => {
    if (!missingAddress) return undefined;
    const electedAddressLabel = !isMilitaryBase 
      ? ([electedAddress.neighbourhood, electedAddress.subDivision, electedAddress.provinceOrState]
        .filter(Boolean)
        .join(', ') || electedAddress.country)
      : '';
    return t(LOCALE_COMMON_PATHS.AREA_PAGE_SUBHEADING_MISSING_ADDRESS, {
      missingAddress,
      electedArea: electedAddressLabel,
    });
  }, [missingAddress, electedAddress, isMilitaryBase, t]);

  // Track listing view failure when redirected from non-existent listing
  const hasTrackedMissingListingRef = useRef(false);
  useEffect(() => {
    if (hasTrackedMissingListingRef.current || !missingAddress) return;

    hasTrackedMissingListingRef.current = true;

    ListingObservabilityService.trackViewFailure({
      listingData: { id: 0, address: missingAddress },
      error: 'Listing does not exist or has been deleted',
      errorCategory: 'not_found',
    });
  }, [missingAddress]);

  // Derived from props — computed during render rather than stored via an effect.
  const fallbackSubheading = useMemo(() => {
    if (!isFallbackAreaPage) return undefined;

    const requestedAddressLabel = [requestedAddress.neighbourhood?.name, requestedAddress.subDivision?.name, requestedAddress.provinceOrState?.name]
      .filter(Boolean)
      .join(', ');
    const electedAddressLabel = [electedAddress.neighbourhood, electedAddress.subDivision, electedAddress.provinceOrState]
      .filter(Boolean)
      .join(', ') || electedAddress.country;
    const useTheArticle = electedAddressLabel === 'USA';

    const localizedSubheading = generateLocalizedSubheading({
      count: 0,
      hasNoListingsInArea: true,
      requestedAreaLabel: requestedAddressLabel,
      electedAreaLabel: electedAddressLabel,
      useTheArticle,
    });

    // Keep legacy message for backwards compatibility
    const grammar_prefix = useTheArticle ? 'the ' : '';
    const noListingsInAreaMessage = requestedAddressLabel && requestedAddressLabel.length > 0
      ? `0 Listings Found / We couldn't find any real estate listings in ${requestedAddressLabel}. Here are some listings in ${grammar_prefix}${electedAddressLabel}`
      : `0 Listings Found / We couldn't find listings matching the criteria but here are some in ${grammar_prefix}${electedAddressLabel}...`;

    return { localizedSubheading, noListingsInAreaMessage };
  }, [isFallbackAreaPage, requestedAddress, electedAddress]);

  const localizedSubheading: LocalizedSubheadingData | undefined = fallbackSubheading?.localizedSubheading;
  const noListingsInAreaMessage: string | undefined = fallbackSubheading?.noListingsInAreaMessage;

  // Analytics is a genuine side effect, so it stays in an effect.
  useEffect(() => {
    if (isFallbackAreaPage) {
      trackEvent(GTM_CLICK_AREA_PAGE_NO_RESULTS);
    }
  }, [isFallbackAreaPage, requestedAddress, electedAddress]);

  const internalLinksHeading = useIsMobile() ? 'Explore More Listings' : '';

  // Remove missingAddress query parameter from URL after it has been read
  if (isClientSide() && missingAddress) {
    const url = new URL(window.location.href);
    url.searchParams.delete('missingAddress');
    const newUrl = url.pathname + url.search;
    window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, '', newUrl);
  }

  // we only want to run this once to restore the scroll position after the component's initial render
  useEffect(() => {
    trackEvent(GTM_VIEW_AREA_PAGE);

    // We set this when user wants to temporarily disable (ignore) that user setting here in area page (instead of user profile)
    // ignoreProfileImagelessSetting is mainly for server side to detect when not to use the filter 'hasImage'
    const ignoreProfileImagelessSetting = Cookies.get(AREA_PAGE_IGNORE_PROFILE_SETTINGS_IMAGELESS_COOKIE_NAME);
    // We can clear it in client-side
    if (ignoreProfileImagelessSetting) Cookies.remove(AREA_PAGE_IGNORE_PROFILE_SETTINGS_IMAGELESS_COOKIE_NAME);
  }, []);

  const captureLastSearch = useCaptureLastSearch();
  const showQuebecPopup = useShowQuebecPopup();
  const hasQuebecContent = !isMilitaryBase && requestedAddress.provinceOrState?.slug === provinceOrStateSlugFromCode('qc');

  useEffect(() => {
    showQuebecPopup(hasQuebecContent, 'area');
  }, [hasQuebecContent, showQuebecPopup]);

  const onFiltersChanged = useCallback((changedFilters: PartialDeep<Filter>, changedFilter?: string) => {
    // Clean up any unwanted properties that trigger a filter change
    const sanitizedFilter = Object.keys(changedFilters)
      .filter(key => !IGNORED_FILTER_PROPERTIES.includes(key as FilterKeys))
      .reduce((obj, key) => {
        return {
          ...obj,
          [key]: changedFilters[key as FilterKeys],
        };
      }, {} as PartialDeep<Filter>);

    const consolidatedFilter = deepmerge(listingParams.filter, sanitizedFilter);
    // We need to merge the consolidated filters with the default filter values since in a lot of places the code expects
    // all filter values to be set even if they are not relevant to the component accessing it.
    const listingParamsFilter = deepmerge(DEFAULT_LISTING_PARAMS_FILTER_SHARED, consolidatedFilter as Filter);
    const cookieFiltersClone: Filter = cloneDeep(listingParams.filter);
    const listingParamsFilterClone: Filter = cloneDeep(listingParamsFilter);
    IGNORED_FILTER_PROPERTIES.forEach(key => {
      delete cookieFiltersClone[key];
      delete listingParamsFilterClone[key];
    });

    const filterDiff = diff(cookieFiltersClone, listingParamsFilterClone) as Filter;

    if (filterDiff?.status) {
      const updatedStatus = filterDiff.status as string;
      if (updatedStatus === AVAILABLE_STATUS) {
        trackEvent(AREA_PAGE_FILTER_OPTION_TO_GTM_EVENT['active']);
      } else if (updatedStatus === NOT_AVAILABLE_OTHER_STATUS) {
        trackEvent(AREA_PAGE_FILTER_OPTION_TO_GTM_EVENT['expired']);
      }
    }

    // If user wants to temporarily turn on hasImage filter and ignore his/her user setting,
    // then we would detect a mismatch, and send a new cookie so when page loads (after the filter change),
    // we know not to use hasImage in user profile when fetching listings on server side
    if (filterDiff?.hasImage !== user?.hideImagelessListings) {
      Cookies.set(AREA_PAGE_IGNORE_PROFILE_SETTINGS_IMAGELESS_COOKIE_NAME, 'true');
    }

    if (Object.keys(filterDiff).length !== 0) {
      // This will trigger a rerender as this component is an observer for the Mobx storage and it is necessary because
      // when we navigate to the map page, we reset the smart url and go back to the /search route. That way the map component
      // depends on the listingParams to figure out the current filters
      listingParams.setFilter(listingParamsFilter);
      captureLastSearch(user?.id, listingParams);
      refreshCurrentLocationFilter(listingParams.filter);

      const targetedUrlConfig: TargetedUrlHelper = {
        url: `${window.location.origin}${window.location.pathname}`,
        filters: consolidatedFilter as Filter,
        sort: sourceSort,
        pageNumber: 1, // When filters change, we want to reset the page number to 1
        fallbackSlug: '',
      };

      const targetedPath = getTargetedUrl(targetedUrlConfig);
      push(targetedPath);
    }

    if (changedFilter) {
      trackEvent(AREA_PAGE_FILTER_OPTION_TO_GTM_EVENT[changedFilter]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingParams, user?.id, sourceSort, push, captureLastSearch, refreshCurrentLocationFilter]);

  const onSortChanged = useCallback((newSort: Sort) => {
    if (sourceSort != newSort) {
      listingParams.setSort(newSort);
      captureLastSearch(user?.id, listingParams);
      const targetedUrlConfig: TargetedUrlHelper = {
        url: `${window.location.origin}${window.location.pathname}`,
        filters: listingParams.filter,
        sort: listingParams.sort,
        pageNumber: 1, // When sorting change, we want to reset the page number to 1
        fallbackSlug: isFallbackAreaPage ? electedAddress?.slug : '',
      };
      const targetedPath = getTargetedUrl(targetedUrlConfig);
      push(targetedPath);
    }
  }, [sourceSort, listingParams, captureLastSearch, user?.id, isFallbackAreaPage, electedAddress?.slug, push]);

  const setPageNumber = useCallback((pageNumber: number) => {
    if (pageNumber != paginationData.page) {
      const targetedUrlConfig: TargetedUrlHelper = {
        url: `${window.location.origin}${window.location.pathname}`,
        filters: listingParams.filter,
        sort: listingParams.sort,
        pageNumber,
        fallbackSlug: isFallbackAreaPage ? electedAddress?.slug : '',
      };
      const targetedPath = getTargetedUrl(targetedUrlConfig);
      push(targetedPath);
    }
  }, [paginationData.page, listingParams.filter, listingParams.sort, isFallbackAreaPage, electedAddress?.slug, push]);

  const handleDeprecationWarningButtonClicked = useCallback(() => {
    setFilterDeprecationAck(true);
    setShowFilterDeprecationWarning(false);
  }, [setShowFilterDeprecationWarning]);

  const handleNoImageListingsFilterWarningClose = useCallback(() => {
    setNoImageListingsFilterAck(true);
    setShowNoImageListingsFilterWarning(false);
  }, [setShowNoImageListingsFilterWarning]);

  // Handle tracking when we show the map card on the area page as part of a growthbook experiment
  const trackMapCardExperimentView = useCallback(() => {
    // Use the showMapCardExperimentView from the server side rather than the useExperiment hook from the client side
    // to determine what event to track since the experiment takes time to load on the client side, meaning the
    // base variation will get tracked even if the experiment is on
    if (showMapCardExperimentView) {
      trackEvent(GTM_AREA_PAGE_MAP_CARD_FOURTH_VERSION_B);
    }
    else {
      trackEvent(GTM_AREA_PAGE_MAP_CARD_FOURTH_VERSION_A);
    }
  }, [showMapCardExperimentView]);

  const trackMapCardClick = useCallback(() => { // This one is for a secondary way to go to the map within the listing grid
    trackEvent(GTM_AREA_PAGE_MAP_CARD_FOURTH_CLICK);
    if (isMilitaryBase) {
      navigateToMilitaryBaseMapView(listingParams, militaryPath, push, siteLocation);
    } else {
      navigateToAddressMapView(listingParams, electedAddress, push, siteLocation);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openSavedSearchFiltersModal = useCallback(() => {
    openModal(SAVED_SEARCH_FILTERS_MODAL, {
      filter: listingParams.filter,
      onFiltersChanged,
      showNoImageListingsFilterWarning,
      onNoImageListingsFilterWarningClose: handleNoImageListingsFilterWarningClose,
    });
  }, [openModal, listingParams.filter, onFiltersChanged, showNoImageListingsFilterWarning, handleNoImageListingsFilterWarningClose]);

  const onMoreButtonClick = useCallback(() => {
    const handleMoreButtonClick = withEventTracking(GTM_CLICK_AREA_PAGE_MORE_FILTERS_BUTTON, openSavedSearchFiltersModal);
    handleMoreButtonClick();
  }, [openSavedSearchFiltersModal]);

  const onSaveSearchButtonClick = useCallback(() => {
    const handleSaveSearchButtonClick = withEventTracking(GTM_CLICK_AREA_PAGE_SAVE_SEARCH_BUTTON, () => {
      if (isAuthenticated) {
        const saveWithFetchedPosition = async () => { // Sets position if user lands on area page with URL
          if (listingParams.filter.slug || query?.areaPath?.[0]) {
            // TODO: In the future, move this to go-search polygon endpoint instead of google.
            const position = await getPositionFromDescription(listingParams.filter.slug || query?.areaPath?.[0], true);
            const latitude = position?.latitude;
            const longitude = position?.longitude;
            if (latitude && longitude) {
              listingParams.setFilter({
                ...listingParams.filter,
                latitude,
                longitude,
              });
              openModal(CREATE_SAVED_SEARCH_MODAL, { query: { ...listingParams, filter: { ...listingParams.filter, boundary: null, latitude, longitude }}});
            }
          } else if (militaryBase) {
            const position = await getPoiPositionFromDescription(militaryBase.slug, true);
            const latitude = position?.latitude;
            const longitude = position?.longitude;
            if (latitude && longitude) {
              listingParams.setFilter({
                ...listingParams.filter,
                latitude,
                longitude,
              });
              openModal(CREATE_SAVED_SEARCH_MODAL, { query: { ...listingParams, filter: { ...listingParams.filter, boundary: null, latitude, longitude }}});
            }
          }
        };
        saveWithFetchedPosition();
      } else {
        openModal(LOGIN_REGISTRATION_MODAL);
      }
    });
    handleSaveSearchButtonClick();
  }, [isAuthenticated, listingParams, query?.areaPath, militaryBase, openModal]);
  const onSearchResultClick = useSearchResultClick();

  const handleMapIconClick = withEventTracking(GTM_CLICK_AREA_PAGE_MAP_BUTTON, () => {
    if (isMilitaryBase) {
      navigateToMilitaryBaseMapView(listingParams, militaryPath, push, siteLocation);
    } else {
      navigateToAddressMapView(listingParams, electedAddress, push, siteLocation);
    }
  });

  let showsMyLinkMyLeadCreate = false;
  let showsMyLinkMyLead = false;

  if (myLinkMyLead) {
    showsMyLinkMyLeadCreate = !!Cookies.get(MLML_AGENT_COOKIE_NAME);
    showsMyLinkMyLead = !!Cookies.get(MLML_LEAD_COOKIE_NAME);
  }

  return (<PageView
    electedAddress={electedAddress}
    listingParams={listingParams}
    listings={listingsData}
    useListingCardWithCompliance={useListingCardWithCompliance}
    listingsWithCompliance={listingsWithReactiveCompliance}
    breadcrumbs={breadcrumbs}
    pagination={paginationData}
    sort={sourceSort}
    heading={heading}
    localizedHeading={localizedHeading}
    localizedSubheading={localizedSubheading}
    areaName={areaName}
    areaBlurb={areaBlurb}
    areaBlurbHeading={areaBlurbHeading}
    areaOverviewTitle={areaOverviewTitle}
    areaNamedContent={areaNamedContent}
    areaInsightsData={areaInsightsData}
    internalLinks={internalLinks}
    internalLinksHeading={internalLinksHeading}
    seoLinks={seoLinks}
    noListingsInAreaMessage={noListingsInAreaMessage}
    hasNoListingsInArea={isFallbackAreaPage}
    missingAddressMessage={missingAddressMessage}
    expandGuidesByDefault={expandGuidesByDefault}
    showGuides={showGuides}
    showDisclaimer={showDisclaimer}
    setPageNumber={setPageNumber}
    onSortChanged={onSortChanged}
    onMobileFilterButtonClick={openSavedSearchFiltersModal}
    onValueChange={onFiltersChanged}
    onSaveSearchButtonClick={onSaveSearchButtonClick}
    onLocationSearchClick={onSearchResultClick}
    onMapIconClick={handleMapIconClick}
    onMoreButtonClick={onMoreButtonClick}
    showFilterDeprecationWarning={showFilterDeprecationWarning}
    showNoImageListingsFilterWarning={showNoImageListingsFilterWarning}
    onDeprecationWarningButtonClick={handleDeprecationWarningButtonClicked}
    onNoImageListingsFilterWarningClose={handleNoImageListingsFilterWarningClose}
    showMapCardExperimentView={showMapCardExperimentView}
    trackMapCardExperimentView={trackMapCardExperimentView}
    trackMapCardClick={trackMapCardClick}
    showsMyLinkMyLeadCreate={showsMyLinkMyLeadCreate}
    showsMyLinkMyLead={showsMyLinkMyLead}
    useLayoutV2={useLayoutV2}
  />);
};


//#region Helper functions
function generateAreaOverviewTitle(address: SingleAddress) {
  if (address.addressType === AddressType.ADDRESS_TYPE_PROVINCE_OR_STATE) {
    return isUsStateCode(address.provinceOrState?.toUpperCase() as ProvinceOrStateCode) ? 'State Guide' : 'Province Guide';
  } else if (address.addressType === AddressType.ADDRESS_TYPE_NEIGHBOURHOOD) {
    return 'Neighbourhood Guide';
  } else {
    return 'City Guide';
  }
}

//#endregion
export default AreaListingsPageController;

export { AreaListingsPageController };